/**
 * Built-in cartoon builder used until @nanome-lite/ribbon (the full RibbonBuilder.cs port) is wired in through
 * engine.setRibbonBuilder(). It follows the same algorithm outline with the RIBBON constants from @nanome-lite/look:
 *  - segments split at chain breaks, non-consecutive residue serials, missing Cα/O (C5'/C3'), Cα–Cα >= 5 Å
 *    (RenderingViewModel.Ribbon.cs:711-713, 839-880)
 *  - uniform cubic B-spline through Cα with control points (i-2, i-1, i, i+1) (RibbonBuilder.cs:132-135, 451-472)
 *  - G-vector orientation: n = cross(cross(CA_i - CA_{i-1}, O_{i-1} - CA_{i-1}), CA_i - CA_{i-1}), sign-continuous,
 *    g_i = normalize(g_{i-1} + n), g_0 = g_1 (FillGVectorJob :320-347)
 *  - helix/sheet half-width 0.9, half-height 0.075; coil 0.1275 square with rounded normals; sheet arrow 1.575 over
 *    the last residue's second half (:350-422, 532-588)
 *  - style map Helix3/Helix5/AlphaHelix/Nucleic_Helix -> helix-like, Strand|BetaBridge -> Sheet, else Coil (:931-943)
 * Not ported here: nucleic base slabs, the sheet smoothing quirk, flat-strip variant. Deterministic and worker-safe.
 */
import type { NanomeModel } from '@nanome-lite/data';
import { isNucleic } from '@nanome-lite/data/model';
import { CartoonStyle, type RibbonBuildOptions, type RibbonMeshBuffers } from '@nanome-lite/ribbon';
import type { RibbonBuilder, RibbonColorFn } from '@nanome-lite/state';
import { RIBBON, SecondaryStructure } from '@nanome-lite/look';

/** The builder signature is the host contract in packages/state/src/engine.ts (buildRibbonMeshes in @nanome-lite/ribbon matches it). */
export type { RibbonBuilder, RibbonColorFn };

type V3 = [number, number, number];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mul = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: V3) => Math.sqrt(dot(a, a));
const norm = (a: V3): V3 => { const l = len(a); return l > 1e-9 ? mul(a, 1 / l) : [0, 0, 1]; };

/** SS -> CartoonStyle (RenderingViewModel.Ribbon.cs:931-943). */
export function cartoonStyle(ss: number): number {
  switch (ss) {
    case SecondaryStructure.Helix3: return CartoonStyle.Helix3;
    case SecondaryStructure.Helix5: return CartoonStyle.Helix5;
    case SecondaryStructure.AlphaHelix: return CartoonStyle.Helix;
    case SecondaryStructure.Nucleic_Helix: return CartoonStyle.NucleicHelix;
    case SecondaryStructure.Strand: case SecondaryStructure.BetaBridge: return CartoonStyle.Sheet;
    default: return CartoonStyle.Coil;
  }
}

interface Residue { index: number; p1: V3; p2: V3; style: number }

function findAtom(model: NanomeModel, residueIndex: number, names: string[]): number {
  const start = model.residues.atomStart[residueIndex]!, count = model.residues.atomCount[residueIndex]!;
  for (const name of names) for (let i = start; i < start + count; i++) if (model.atomName[i] === name) return i;
  return -1;
}
const posOf = (m: NanomeModel, i: number): V3 => [m.xyz[i * 3]!, m.xyz[i * 3 + 1]!, m.xyz[i * 3 + 2]!];

/** Split residues into continuous segments. */
export function extractSegments(model: NanomeModel, residueIndices: Uint32Array): Residue[][] {
  const segs: Residue[][] = [];
  let cur: Residue[] = [];
  let prevChain = -1, prevSerial = NaN, prevP1: V3 | null = null;
  const flush = () => { if (cur.length >= 2) segs.push(cur); cur = []; };
  for (const ri of residueIndices) {
    const nucleic = isNucleic(model.residues.substructure[ri]!);
    const a1 = findAtom(model, ri, nucleic ? ["C5'"] : ['CA']);
    const a2 = findAtom(model, ri, nucleic ? ["C3'"] : ['O', 'OXT']);
    const chain = model.residues.chainIndex[ri]!;
    const serial = model.residues.serial[ri]!;
    if (a1 < 0 || a2 < 0) { flush(); prevChain = chain; prevSerial = serial; prevP1 = null; continue; }
    const p1 = posOf(model, a1);
    const gap = chain !== prevChain || (Number.isFinite(prevSerial) && serial !== prevSerial && serial !== prevSerial + 1) || (prevP1 !== null && len(sub(p1, prevP1)) >= RIBBON.gapDistance);
    if (gap) flush();
    cur.push({ index: ri, p1, p2: posOf(model, a2), style: nucleic ? CartoonStyle.NucleicHelix : cartoonStyle(model.residues.ss[ri]!) });
    prevChain = chain; prevSerial = serial; prevP1 = p1;
  }
  flush();
  return segs;
}

function halfSize(style: number): [number, number] {
  switch (style) {
    case CartoonStyle.Coil: return [RIBBON.coilHalfSize, RIBBON.coilHalfSize];
    default: return [RIBBON.helixHalfWidth, RIBBON.helixHalfHeight];
  }
}

const B = RIBBON.bsplineBasis;
function bspline(p0: V3, p1: V3, p2: V3, p3: V3, t: number): V3 {
  const t2 = t * t, t3 = t2 * t;
  const w = [
    (B[0][0] * t3 + B[1][0] * t2 + B[2][0] * t + B[3][0]) / 6,
    (B[0][1] * t3 + B[1][1] * t2 + B[2][1] * t + B[3][1]) / 6,
    (B[0][2] * t3 + B[1][2] * t2 + B[2][2] * t + B[3][2]) / 6,
    (B[0][3] * t3 + B[1][3] * t2 + B[2][3] * t + B[3][3]) / 6,
  ];
  return [
    w[0]! * p0[0] + w[1]! * p1[0] + w[2]! * p2[0] + w[3]! * p3[0],
    w[0]! * p0[1] + w[1]! * p1[1] + w[2]! * p2[1] + w[3]! * p3[1],
    w[0]! * p0[2] + w[1]! * p1[2] + w[2]! * p2[2] + w[3]! * p3[2],
  ];
}

export function buildSegment(seg: Residue[], colorOf: RibbonColorFn, steps: number): RibbonMeshBuffers {
  const n = seg.length;
  // G-vectors
  const g: V3[] = new Array(n);
  for (let i = 1; i < n; i++) {
    const d = sub(seg[i]!.p1, seg[i - 1]!.p1);
    const o = sub(seg[i - 1]!.p2, seg[i - 1]!.p1);
    let nn = norm(cross(cross(d, o), d));
    if (i > 1 && dot(nn, g[i - 1]!) < 0) nn = mul(nn, -1);
    g[i] = i === 1 ? nn : norm(add(g[i - 1]!, nn));
  }
  g[0] = g[1] ?? [0, 0, 1];
  // Sheet arrow flags: last residue of a sheet run (next residue not a sheet)
  const arrow = seg.map((r, i) => r.style === CartoonStyle.Sheet && (i === n - 1 || seg[i + 1]!.style !== CartoonStyle.Sheet));
  const P = (i: number) => seg[Math.min(n - 1, Math.max(0, i))]!.p1;

  const positions: number[] = [], normals: number[] = [], colors: number[] = [], residue: number[] = [], index: number[] = [];
  let ringBase = -1;
  let prevRight: V3 | null = null;
  const pushRing = (c: V3, tangent: V3, gv: V3, hw: number, hh: number, rounded: number, color: V3, resIdx: number) => {
    let right = norm(sub(gv, mul(tangent, dot(gv, tangent))));
    if (prevRight && dot(right, prevRight) < 0) right = mul(right, -1);
    prevRight = right;
    const up = norm(cross(tangent, right));
    const corners: V3[] = [add(add(c, mul(right, hw)), mul(up, hh)), add(sub(c, mul(right, hw)), mul(up, hh)), sub(sub(c, mul(right, hw)), mul(up, hh)), sub(add(c, mul(right, hw)), mul(up, hh))];
    const faceN: V3[] = [up, mul(right, -1), mul(up, -1), right]; // top, left, bottom, right faces
    const base = positions.length / 3;
    // 8 vertices: each corner twice (one per adjacent face) for crisp edges; coil blends toward the rounded normal.
    for (let k = 0; k < 4; k++) {
      const corner = corners[k]!;
      const roundedN = norm(sub(corner, c));
      for (const f of [k, (k + 3) % 4]) {
        const nf = faceN[f]!;
        const nn = norm(add(mul(nf, 1 - rounded), mul(roundedN, rounded)));
        positions.push(corner[0], corner[1], corner[2]);
        normals.push(nn[0], nn[1], nn[2]);
        colors.push(color[0], color[1], color[2]);
        residue.push(resIdx);
      }
    }
    if (ringBase >= 0) {
      // faces: for face k, vertices (corner k, face k) and (corner k+1, face k) on both rings
      for (let k = 0; k < 4; k++) {
        const a0 = ringBase + k * 2, b0 = ringBase + ((k + 1) % 4) * 2 + 1;
        const a1 = base + k * 2, b1 = base + ((k + 1) % 4) * 2 + 1;
        index.push(a0, b0, a1, b0, b1, a1);
      }
    }
    ringBase = base;
    return base;
  };
  const cap = (base: number, flip: boolean) => {
    // close the ring with two triangles over corners (0..3) using the "right" face copies
    const v = [base + 0, base + 2, base + 4, base + 6];
    if (flip) index.push(v[0]!, v[2]!, v[1]!, v[0]!, v[3]!, v[2]!); else index.push(v[0]!, v[1]!, v[2]!, v[0]!, v[2]!, v[3]!);
  };

  let firstBase = -1;
  for (let i = 1; i < n; i++) {
    const c0 = colorOf(seg[i - 1]!.index), c1 = colorOf(seg[i]!.index);
    const [hw0, hh0] = halfSize(seg[i - 1]!.style), [hw1, hh1] = halfSize(seg[i]!.style);
    const r0 = seg[i - 1]!.style === CartoonStyle.Coil ? 1 : 0, r1 = seg[i]!.style === CartoonStyle.Coil ? 1 : 0;
    const last = i === n - 1;
    const count = last ? steps + 1 : steps;
    for (let s = 0; s < count; s++) {
      const t = s / steps;
      const c = bspline(P(i - 2), P(i - 1), P(i), P(i + 1), t);
      const cNext = bspline(P(i - 2), P(i - 1), P(i), P(i + 1), Math.min(1, t + 1e-3));
      const cPrev = bspline(P(i - 2), P(i - 1), P(i), P(i + 1), Math.max(0, t - 1e-3));
      const tangent = norm(sub(cNext, cPrev));
      const gv = norm(add(mul(g[i - 1]!, 1 - t), mul(g[i]!, t)));
      let hw = hw0 * (1 - t) + hw1 * t;
      const hh = hh0 * (1 - t) + hh1 * t;
      if (arrow[i]) hw = t < 0.5 ? RIBBON.arrowHalfWidth : RIBBON.arrowHalfWidth * (1 - (t - 0.5) * 2) + 0.05 * (t - 0.5) * 2;
      else if (arrow[i - 1] && seg[i]!.style !== CartoonStyle.Sheet) hw = hw0 * 0 + hw1; // after an arrow tip the next residue starts at its own width
      const rounded = r0 * (1 - t) + r1 * t;
      const color: V3 = [c0[0] * (1 - t) + c1[0] * t, c0[1] * (1 - t) + c1[1] * t, c0[2] * (1 - t) + c1[2] * t];
      const base = pushRing(c, tangent, gv, hw, hh, rounded, color, t < 0.5 ? seg[i - 1]!.index : seg[i]!.index);
      if (firstBase < 0) { firstBase = base; cap(base, true); }
      if (last && s === count - 1) cap(base, false);
    }
  }
  return {
    position: Float32Array.from(positions), normal: Float32Array.from(normals), color: Float32Array.from(colors),
    residueIndex: Uint32Array.from(residue), index: Uint32Array.from(index), vertexCount: positions.length / 3, triangleCount: index.length / 3,
  };
}

/** Fallback RibbonBuilder: one mesh per continuous segment. */
export const fallbackRibbonBuilder: RibbonBuilder = (model, residueIndices, colorOf, options) =>
  extractSegments(model, residueIndices).map((seg) => buildSegment(seg, colorOf, options.stepsPerResidue));
